import { useEffect, useId, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { AuthShell } from './auth-shell';
import { Button } from '@/components/ui/button';
import { FormField, Input } from '@/components/ui/field';
import { messageFor } from '@/api/errors';
import { requestLoginChallenge, verifyMfa } from '@/auth/auth-api';
import { landingPathFor } from '@/auth/rbac';
import { getSession } from '@/auth/session';

/**
 * Admin sign-in (03 §12, 06 §3).
 *
 * ⚠️ **Two steps, and the first one grants nothing.** A correct password
 * returns an MFA challenge token, which is not an access token — TOTP is
 * mandatory for every admin with no opt-out. The screen is modelled on that:
 * there is no state in which this component holds a session before the code has
 * been verified, so no future edit can accidentally produce one.
 */
type Stage =
  | { name: 'credentials' }
  | {
      name: 'mfa';
      mfaToken: string;
      email: string;
      expiresAt: number;
      /** Present only for an admin who has never paired — see auth-api. */
      enrolment?: { otpauthUri: string; secret: string };
    };

export function LoginPage(): React.JSX.Element {
  const [stage, setStage] = useState<Stage>({ name: 'credentials' });
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    // A live session has no business on the sign-in screen; send them on.
    const session = getSession();
    if (session) void navigate(landingPathFor(session.role), { replace: true });
  }, [navigate]);

  const onAuthenticated = (): void => {
    const session = getSession();
    void navigate(session ? landingPathFor(session.role) : '/', { replace: true });
  };

  if (stage.name === 'mfa') {
    return (
      <MfaStep
        stage={stage}
        onBack={() => setStage({ name: 'credentials' })}
        onVerified={onAuthenticated}
      />
    );
  }

  return (
    <CredentialsStep
      idleNotice={params.get('reason') === 'idle'}
      resetNotice={params.get('reason') === 'password-reset'}
      onChallenge={(mfaToken, email, expiresIn, enrolment) =>
        setStage({
          name: 'mfa',
          mfaToken,
          email,
          expiresAt: Date.now() + expiresIn * 1000,
          enrolment,
        })
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Step 1 — credentials
// ---------------------------------------------------------------------------

function CredentialsStep({
  onChallenge,
  idleNotice,
  resetNotice,
}: {
  onChallenge: (
    mfaToken: string,
    email: string,
    expiresIn: number,
    enrolment?: { otpauthUri: string; secret: string },
  ) => void;
  idleNotice: boolean;
  /** Set after a completed password reset, which lands here rather than signing in. */
  resetNotice: boolean;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const emailId = useId();
  const passwordId = useId();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const challenge = await requestLoginChallenge(email.trim(), password);
      onChallenge(challenge.mfaToken, email.trim(), challenge.expiresIn, challenge.enrolment);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      description="Your password is the first of two steps. You will be asked for a code from your authenticator app."
      footer={
        <>
          <Link to="/forgot-password" className="underline underline-offset-2">
            Forgot your password?
          </Link>
          <span className="mt-1 block">
            Admin accounts are created by invitation. There is no self-registration.
          </span>
        </>
      }
    >
      {resetNotice ? (
        <p
          role="status"
          className="mb-5 rounded-md border border-success/30 bg-success-wash px-3 py-2 text-xs text-success"
        >
          Your password has been changed. Sign in with your new password and your authenticator
          code.
        </p>
      ) : null}

      {idleNotice ? (
        <p
          role="status"
          className="mb-5 rounded-md border border-caution/30 bg-caution-wash px-3 py-2 text-xs text-caution"
        >
          You were signed out after 30 minutes of inactivity.
        </p>
      ) : null}

      <form
        onSubmit={(event) => {
          void submit(event);
        }}
        className="space-y-4"
        noValidate
      >
        <FormField label="Email" htmlFor={emailId} required>
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </FormField>

        <FormField label="Password" htmlFor={passwordId} required>
          <Input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </FormField>

        {error ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
          Continue
        </Button>
      </form>
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — TOTP
// ---------------------------------------------------------------------------

function MfaStep({
  stage,
  onBack,
  onVerified,
}: {
  stage: {
    mfaToken: string;
    email: string;
    expiresAt: number;
    enrolment?: { otpauthUri: string; secret: string };
  };
  onBack: () => void;
  onVerified: () => void;
}): React.JSX.Element {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.round((stage.expiresAt - Date.now()) / 1000)),
  );
  const codeId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSecondsLeft(Math.max(0, Math.round((stage.expiresAt - Date.now()) / 1000)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [stage.expiresAt]);

  const expired = secondsLeft <= 0;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await verifyMfa(stage.mfaToken, code);
      onVerified();
    } catch (err) {
      setError(messageFor(err));
      setCode('');
      inputRef.current?.focus();
    } finally {
      setPending(false);
    }
  };

  const enrolling = stage.enrolment !== undefined;

  return (
    <AuthShell
      title={enrolling ? 'Set up two-step verification' : 'Two-step verification'}
      description={
        enrolling
          ? `Scan this with an authenticator app, then enter the code it shows for ${stage.email}.`
          : `Enter the 6-digit code from your authenticator app for ${stage.email}.`
      }
    >
      {/* ⚠️ Shown only when the API returned enrolment material, which it does
          only for an admin who has never paired. An enrolled admin never sees a
          QR here — if a correct password could re-issue one, a phished password
          would be enough to pair a new device and MFA would protect nothing. */}
      {stage.enrolment ? (
        <EnrolmentPanel otpauthUri={stage.enrolment.otpauthUri} secret={stage.enrolment.secret} />
      ) : null}

      <form
        onSubmit={(event) => {
          void submit(event);
        }}
        className="space-y-4"
        noValidate
      >
        <FormField
          label="Authentication code"
          htmlFor={codeId}
          hint="Codes change every 30 seconds."
          error={error ?? undefined}
          required
        >
          <Input
            ref={inputRef}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            placeholder="000000"
            className="h-12 text-center font-mono text-xl tracking-[0.5em]"
            disabled={expired}
            required
          />
        </FormField>

        {expired ? (
          <p role="alert" className="text-xs font-medium text-danger">
            This challenge has expired. Enter your password again to get a new one.
          </p>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-ink-faint">
            <ShieldCheck className="size-3.5" aria-hidden="true" />
            Challenge expires in <time data-numeric>{formatCountdown(secondsLeft)}</time>
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={pending}
          disabled={expired || code.length !== 6}
        >
          Verify and sign in
        </Button>

        <Button type="button" variant="ghost" size="sm" className="w-full" onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
          Use a different account
        </Button>
      </form>
    </AuthShell>
  );
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The QR code an admin scans the first time they sign in.
 *
 * ⚠️ Also prints the secret in text. Scanning is the path that works, but an
 * admin on a desktop authenticator, or one whose camera is refused by the
 * browser, needs to type it — and the grouped-for-reading form is NOT what they
 * should type. Showing the raw string is what stops "invalid key" from an app
 * that will not strip spaces.
 */
function EnrolmentPanel({
  otpauthUri,
  secret,
}: {
  otpauthUri: string;
  secret: string;
}): React.JSX.Element {
  const qr = useLoginQrCode(otpauthUri);

  return (
    <div className="mb-6 rounded-lg border border-line bg-surface-sunken p-4">
      <div className="flex flex-wrap items-start gap-4">
        {qr ? (
          <img
            src={qr}
            alt="QR code for pairing your authenticator app"
            width={132}
            height={132}
            className="rounded bg-white p-1"
          />
        ) : (
          <div className="size-[132px] animate-pulse rounded bg-surface" />
        )}

        <div className="min-w-0 flex-1 space-y-2 text-xs text-ink-soft">
          <p>Scan with Google Authenticator, 1Password, Authy or similar.</p>
          <p>Can&apos;t scan? Enter this key exactly, with no spaces:</p>
          <code className="block rounded bg-surface px-2 py-1 font-mono text-2xs break-all text-ink">
            {secret}
          </code>
          <p>Your authenticator is paired by the first code you enter below.</p>
        </div>
      </div>
    </div>
  );
}

function useLoginQrCode(uri: string): string | null {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void QRCode.toDataURL(uri, { margin: 1, width: 264, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      // The printed key above is the fallback, so a failed render costs
      // convenience rather than the ability to enrol at all.
      .catch(() => {
        if (!cancelled) setDataUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [uri]);

  return dataUrl;
}
